import { Box, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDragnDrop from 'components/shared/CustomDragnDrop/CustomDragnDrop';
import CustomStepper from 'components/shared/CustomStepper/CustomStepper';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import { useImagePreview } from 'customHook/useProfilePicture';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientService } from 'service/client.service';
import { LeadsService } from 'service/leads.service';
import { LocationService } from 'service/location.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  clearAddLeadDraft,
  clearEditLeadData,
  getLeadCredits,
  setAddLeadDraft,
  setOpenAddLeadDrawer,
} from 'store/slices/leadsSlice';
import zeplinColor from 'theme/Color';
import LeadDetailsForm from './LeadDetailsForm';
import { useLeadFiles } from '../customHook/useLeadAttachment';

type AddLeadFormProps = {
  onClose: (event: React.KeyboardEvent | React.MouseEvent) => void;
  setDrawerState: (visible: boolean) => void;
  onLeadAdded?: () => void;
  leadType?: string;
  leadTypeId?: string;
  typeDropdowns?: any;
  dropdownsLoading?: boolean;
  dropdownError?: string | null;
};

const getAttachmentKeys = (attachments: any): string[] => {
  if (!attachments) return [];
  if (Array.isArray(attachments)) return attachments.filter((a) => typeof a === 'string');
  if (typeof attachments === 'object' && 'added' in attachments) {
    return Array.isArray(attachments.added)
      ? attachments.added.filter((a: any) => typeof a === 'string')
      : [];
  }
  return [];
};

const AddLeadForm = ({
  setDrawerState,
  onLeadAdded,
  leadType,
  leadTypeId,
  typeDropdowns,
}: AddLeadFormProps) => {
  const theme = useTheme();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { alertState, completeOperation, setAlertState, getAlertContent } = useAlertStatus();

  const {
    addLeadDraft,
    assigneeDropdown,
    editLead,
    isEditMode,
    id: leadId,
    leadCredits,
    loading: leadsLoading,
  } = useAppSelector((state) => state.leads);

  const wait = useRef<any>();

  const [loadingAddress, setLoadingAddress] = useState(false);
  const [addressOptions, setAddressOptions] = useState<{ Name: string; Id: string }[]>([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [generatingAnalysis, setGeneratingAnalysis] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState<{ [k: number]: boolean }>({});
  const [formValues, setFormValues] = useState<Record<string, string>>(
    addLeadDraft?.formValues ?? {},
  );
  const [selectedItems, setSelectedItems] = useState<{
    assignTo: { Name: string; Id: string }[];
    address: { Name: string; Id: string }[];
  }>({
    assignTo: [],
    address: [],
  });

  const [searchingCaptiv8, setSearchingCaptiv8] = useState(false);
  const [captiv8List, setCaptiv8List] = useState<any[]>([]);

  const [selectedIntegrations, setSelectedIntegrations] = useState<{
    captiv8: { Name: string; Id: string }[];
    demographicPro: { Name: string; Id: string }[];
  }>({
    captiv8: [],
    demographicPro: [],
  });
  const currentLeadId = isEditMode ? editLead?.leadId : leadId;
  const [attachmentKeys, setAttachmentKeys] = useState<string[]>([]);
  // Centralized dropdown options state
  const [dropdownOptions, setDropdownOptions] = useState({
    salutation: [] as string[],
    leadStatus: [] as string[],
    level: [] as string[],
    college: [] as string[],
    sport: [] as string[],
    position: [] as string[],
    class: [] as string[],
    draftEligible: [] as string[],
    redshirt: [] as string[],
    draftProjection: [] as string[],
    espnRanking: [] as string[],
    sports247Rating: [] as string[],
  });
  const {
    fileInputRef,
    previewUrl: avatarPreview,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
    selectedFile: defaultProfileImage,
  } = useImagePreview(editLead?.profileImageUrl || null);

  useEffect(() => {
    if (!isEditMode || !editLead) return;
    setAttachmentKeys(getAttachmentKeys((editLead as any)?.attachments));
  }, [isEditMode, editLead]);

  const {
    selectedFiles,
    setSelectedFiles,
    deleteFile,
    onDrop,
    uploadedUrls,
    loading: attachmentLoading,
  } = useLeadFiles(currentLeadId, isEditMode ? attachmentKeys : undefined);

  console.log({ selectedFiles, uploadedUrls });

  const handleAttachmentDrop = async (files: File[] | FileList | any) => {
    const fileList: File[] = Array.isArray(files) ? files : Array.from(files || []);
    if (!fileList.length) return;

    if (currentLeadId) {
      // edit mode: upload immediately via hook + callback
      try {
        await onDrop(fileList);
      } catch {
        completeOperation('upload', 'Failed to upload attachments', true);
      }
      return;
    }

    // add mode (later): local staging only
    setSelectedFiles((prev) => [...prev, ...fileList]);
  };

  const handleAttachmentDelete = async (index: number) => {
    const file = selectedFiles[index];
    if (!file) return;

    // add mode (later): local remove only
    if (!currentLeadId) {
      deleteFile(index);
      return;
    }

    if (typeof file !== 'string') {
      // not persisted yet, local remove only
      deleteFile(index);
      return;
    }

    const originalFiles = [...selectedFiles];
    const nextKeys = attachmentKeys.filter((k) => k !== file);

    deleteFile(index);
    setAttachmentKeys(nextKeys);

    try {
      await LeadsService.updateLead(currentLeadId, {
        attachments: { added: nextKeys, removed: [file] },
      });
      completeOperation('delete', 'Attachment deleted successfully', false);
    } catch {
      setSelectedFiles(originalFiles);
      setAttachmentKeys(attachmentKeys);
      completeOperation('delete', 'Failed to delete attachment', true);
    }
  };

  const handleGenerateAnalysis = async () => {
    try {
      setGeneratingAnalysis(true);
      if (selectedIntegrations.captiv8[0]) {
        const payload = {
          instagram: formValues.demographicInstagram || '',
          twitter: formValues.demographicTwitter || '',
          facebook: formValues.demographicFacebook || '',
          youtube: formValues.demographicYoutube || '',
          tiktok: formValues.demographicTikTok || '',
          captiv8Id: selectedIntegrations.captiv8[0].Id,
        };
        await LeadsService.getAnalysis(payload);
        dispatch(
          setAddLeadDraft({
            formValues,
            selectedItems,
            selectedIntegrations,
            activeStep,
            completed,
          }),
        );
        dispatch(setOpenAddLeadDrawer(true));
        setDrawerState(false);
        navigate('/leads/new-lead/social-insights');
      }
    } catch (error) {
      completeOperation('fetch', 'Failed to generate analysis', true);
    } finally {
      setGeneratingAnalysis(false);
    }
  };

  const suggestionCaptiv8API = async (value: string) => {
    try {
      setSearchingCaptiv8(true);
      const res = await ClientService.searchCaptiv8(value?.toLocaleLowerCase());
      setCaptiv8List(
        res?.users?.map((val: any) => ({
          Id: val?.captiv8Id,
          Name: val?.name,
          avatar: val?.avatar,
          followers: val?.followers,
        })) || [],
      );
    } catch (err) {
      completeOperation('fetch', 'Failed to search Captiv8 users', true);
    } finally {
      setSearchingCaptiv8(false);
    }
  };

  const handleCaptiv8Search = async (e: React.SyntheticEvent, value: string) => {
    clearTimeout(wait.current);
    if (value) {
      const getSuggestion = setTimeout(() => suggestionCaptiv8API(value), 1000);
      wait.current = getSuggestion;
    }
  };

  // Update dropdown options when typeDropdowns changes
  useEffect(() => {
    if (typeDropdowns) {
      setDropdownOptions({
        salutation: typeDropdowns?.salutation || [],
        leadStatus: typeDropdowns?.status || [],
        level: typeDropdowns?.level || [],
        college: typeDropdowns?.college || [],
        sport: typeDropdowns?.sport || [],
        position: typeDropdowns?.position || [],
        class: typeDropdowns?.class || [],
        draftEligible: typeDropdowns?.draft_eligible || [],
        redshirt: typeDropdowns?.redshirt || typeDropdowns?.draft_eligible || [],
        draftProjection: typeDropdowns?.draft_projection || [],
        espnRanking: typeDropdowns?.espn_rank || [],
        sports247Rating: typeDropdowns?.x247_sports_rating || [],
      });
    }
  }, [typeDropdowns]);

  const handleCaptiv8Selection = async (value: any) => {
    const selected = Array.isArray(value) ? value[0] : value;

    setSelectedIntegrations((prev) => ({ ...prev, captiv8: selected ? [selected] : [] }));

    if (!selected?.Id) return;

    try {
      const res = await LeadsService.syncCaptiv8Lead(selected.Id);
      const data = res?.data ?? res;

      setFormValues((prev) => ({
        ...prev,
        demographicInstagram: data?.instagram ?? '',
        demographicTwitter: data?.twitter ?? '',
        demographicFacebook: data?.facebook ?? '',
        demographicYoutube: data?.youtube ?? '',
        demographicTikTok: data?.tiktok ?? '',
      }));
    } catch (err) {
      completeOperation('fetch', 'Failed to sync Captiv8 profile', true);
    }
  };
  const suggestionAPI = async (value: string) => {
    try {
      setLoadingAddress(true);
      const res = await LocationService.getAddressSuggestion(value?.toLocaleLowerCase());
      setAddressOptions(
        res?.map((val: any) => ({ Id: val?.description, Name: val?.description })) || [],
      );
    } catch (err) {
    } finally {
      setLoadingAddress(false);
    }
  };

  const handleAddressSearch = async (e: React.SyntheticEvent, value: string) => {
    clearTimeout(wait.current);
    const getSuggestion = setTimeout(() => suggestionAPI(value), 1000);
    wait.current = getSuggestion;
  };

  const handleAddressSelection = (value: any) => {
    setSelectedItems((prev) => ({ ...prev, address: value ? [value] : [] }));
    const parts = value?.Name?.split(',') || [];
    setFormValues((prev) => ({
      ...prev,
      street: parts[0]?.trim() || '',
      city: parts[parts.length - 3]?.trim() || '',
      state: parts[parts.length - 2]?.trim() || '',
      country: parts[parts.length - 1]?.trim() || '',
    }));
  };
  const handleValueChange =
    (key: string) =>
    (event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
      setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
    };

  const handleSelectChange = (key: string) => (event: any) => {
    setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleAssignSelection = (value: any) => {
    setSelectedItems((prev) => ({ ...prev, assignTo: value ? [value] : [] }));
  };

  const handleNext = () => {
    dispatch(getLeadCredits());
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    setActiveStep((prev) => Math.min(prev + 1, 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const handleCreateLead = async () => {
    if (!formValues.name?.trim()) return;
    let profileImageUrl: string | null = null;
    setCreateLoading(true);
    if (avatarPreview) {
      const result = await uploadProfilePicture({ key: false, type: 'leads' });
      profileImageUrl = result === undefined ? defaultProfileImage : result;
    }
    const assignee = selectedItems.assignTo[0];
    const { Name, ...assigneeData } = assignee || {};
    const payload = {
      name: formValues.name?.trim(),
      salutation: formValues.salutation || undefined,

      assignedTo: assigneeData || undefined,
      agency: formValues.agency?.trim(),
      sport: formValues.sport?.trim(),
      position: formValues.position?.trim(),
      class: formValues.class?.trim(),
      level: formValues.level?.trim(),
      college: formValues.college?.trim(),
      status: formValues.leadStatus,
      draftEligible: formValues.draftEligible,
      redshirt: formValues.redshirt,
      draftProjection: formValues.draftProjection,
      espnRank: formValues.espnRank,
      sports247Rating: formValues.sports247Rating,
      pffOverallGrade: formValues.pffOverallGrade?.trim(),
      decisionMaker: formValues.decisionMaker?.trim(),
      manager: formValues.manager?.trim(),
      referredBy: formValues.referredBy?.trim(),
      email: formValues.email?.trim(),
      phone: formValues.mobile?.trim(),
      address: selectedItems.address[0]?.Name || undefined,
      street: formValues.street?.trim(),
      city: formValues.city?.trim(),
      state: formValues.state?.trim(),
      zip: formValues.zip?.trim(),
      country: formValues.country?.trim(),
      instagram: formValues.instagram?.trim(),
      twitter: formValues.twitter?.trim(),
      leadTypeId,
      leadTypeName: leadType,
      profileImageUrl: profileImageUrl ?? '',
      attachments: uploadedUrls.length ? [...uploadedUrls] : [],
    };

    try {
      setCreateLoading(true);

      if (isEditMode && editLead?.leadId) {
        await LeadsService.updateLead(editLead.leadId, payload);
      } else {
        await LeadsService.createLead({ ...payload, leadId: leadId });
      }

      dispatch(clearAddLeadDraft());
      dispatch(clearEditLeadData());
      dispatch(setOpenAddLeadDrawer(false));
      setDrawerState(false);
      onLeadAdded?.();
    } catch (error) {
      completeOperation('create', 'Failed to refresh leads', true);
    } finally {
      setCreateLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditMode || !editLead) return;

    setFormValues({
      salutation: editLead?.salutation || '',
      name: editLead?.name || '',
      title: editLead?.title || '',
      agency: editLead?.agencyName || editLead?.agency || '',
      sport: editLead?.sport || '',
      position: editLead?.position || '',
      class: editLead?.class || '',
      level: editLead?.level || '',
      college: editLead?.college || '',
      leadStatus: editLead?.status || '',
      draftEligible: editLead?.draftEligible || '',
      redshirt: editLead?.redshirt || '',
      draftProjection: editLead?.draftProjection || '',
      espnRank: editLead?.espnRank || '',
      sports247Rating: editLead?.sports247Rating || '',
      pffOverallGrade: editLead?.pffOverallGrade || '',
      decisionMaker: editLead?.decisionMaker || '',
      manager: editLead?.manager || '',
      referredBy: editLead?.referredBy || '',
      email: editLead?.email || '',
      mobile: editLead?.phone || '',
      street: editLead?.street || '',
      city: editLead?.city || '',
      state: editLead?.state || '',
      zip: editLead?.zip || '',
      country: editLead?.country || '',
      instagram: editLead?.instagram || '',
      twitter: editLead?.twitter || '',
    });

    const assignedId =
      editLead?.assignedTo?.Id || editLead?.assignedTo?.id || editLead?.assignedToId;
    const assignedName =
      editLead?.assignedTo?.Name || editLead?.assignedTo?.name || editLead?.createdBy || '';

    const matchedAssignee =
      assigneeDropdown.find(
        (item: any) =>
          String(item?.Id) === String(assignedId) ||
          item?.Name?.trim().toLowerCase() === assignedName.trim().toLowerCase(),
      ) ||
      (assignedId || assignedName
        ? {
            Id: assignedId || assignedName,
            Name: assignedName,
          }
        : null);

    setSelectedItems({
      assignTo: matchedAssignee ? [matchedAssignee] : [],
      address: editLead?.address
        ? [
            {
              Id: editLead.address,
              Name: editLead.address,
            },
          ]
        : [],
    });
  }, [isEditMode, editLead, assigneeDropdown]);

  const disabled =
    createLoading ||
    !formValues?.name ||
    !selectedItems?.assignTo ||
    !formValues?.leadStatus ||
    generatingAnalysis ||
    attachmentLoading;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: zeplinColor.Neutral1,
          zIndex: 10,
        }}
      >
        <CustomStepper
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          setCompleted={setCompleted}
          completed={completed}
          steps={['Lead Details', 'Social Insights']}
        />
      </Box>

      {activeStep === 0 && (
        <LeadDetailsForm
          formValues={formValues}
          selectedItems={selectedItems}
          dropdownOptions={dropdownOptions}
          assigneeDropdown={assigneeDropdown}
          addressOptions={addressOptions}
          loadingAddress={loadingAddress}
          avatarPreview={avatarPreview}
          fileInputRef={fileInputRef}
          handleValueChange={handleValueChange}
          handleSelectChange={handleSelectChange}
          handleAssignSelection={handleAssignSelection}
          handleAddressSelection={handleAddressSelection}
          handleAddressSearch={handleAddressSearch}
          handleFileChange={handleFileChange}
          handleAttachmentDrop={handleAttachmentDrop}
          attachmentFiles={selectedFiles}
          onAttachmentDelete={handleAttachmentDelete}
          openFileSelection={openFileSelection}
          clearSelection={clearSelection}
          leadId={currentLeadId}
        />
      )}

      {activeStep === 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            flex: 1,
            padding: '24px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Captiv8
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              Select the correct Captiv8 Client handles to sync real-time engagement data.
            </Typography>
          </Box>
          <CustomAutocomplete
            loading={searchingCaptiv8}
            popupIcon='/assets/Search.svg'
            required={false}
            options={captiv8List || []}
            getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
            label={'Captiv8'}
            handleDeleteItems={() => {}}
            selectedItems={selectedIntegrations.captiv8}
            handleSelection={handleCaptiv8Selection}
            value={selectedIntegrations.captiv8[0]}
            handleFilter={handleCaptiv8Search}
            placeholder='Search Captiv8'
            currentLength={captiv8List.length}
            totalLength={captiv8List.length}
            next={() => {}}
            multiple={false}
          />
          <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Typography variant='responsiveHeading4' color={zeplinColor.BasicBlack}>
              Demographic Pro
            </Typography>
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
              Input a correct social handle (@username) to generate a detailed Demographic Pro
              analysis for this athlete.
            </Typography>
          </Box>
          <CustomTextField
            style={{ flex: 1 }}
            name='Instagram'
            text={formValues.demographicInstagram || ''}
            form
            label='Instagram'
            type='text'
            onChange={handleValueChange('demographicInstagram')}
          />
          <CustomTextField
            style={{ flex: 1 }}
            name='Youtube'
            text={formValues.demographicYoutube || ''}
            form
            label='Youtube'
            type='text'
            onChange={handleValueChange('demographicYoutube')}
          />
          <CustomTextField
            style={{ flex: 1 }}
            name='TikTok'
            text={formValues.demographicTikTok || ''}
            form
            label='TikTok'
            type='text'
            onChange={handleValueChange('demographicTikTok')}
          />
          <CustomTextField
            style={{ flex: 1 }}
            name='X(Twitter)'
            text={formValues.demographicTwitter || ''}
            form
            label='X(Twitter)'
            type='text'
            onChange={handleValueChange('demographicTwitter')}
          />
          <CustomTextField
            style={{ flex: 1 }}
            name='Facebook'
            text={formValues.demographicFacebook || ''}
            form
            label='Facebook'
            type='text'
            onChange={handleValueChange('demographicFacebook')}
          />
          <Box
            sx={{
              borderRadius: '14px',
              padding: '12px 16px',
              backgroundColor: zeplinColor.Neutral3,
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
              Demographic pro analysis credits
            </Typography>
            <Box sx={{ display: 'flex', gap: '12px' }}>
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Total credits: {leadCredits?.totalCredits ?? 'N/A'}
              </Typography>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px',
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Used to data: {leadCredits?.usedCredits ?? 'N/A'}
              </Typography>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px',
                }}
              />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Still available: {leadCredits?.availableCredits ?? 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          width: '100%',
          position: 'sticky',
          bottom: '0px',
          margin: 'auto',
          zIndex: '100',
          padding: '24px',
          backgroundColor: zeplinColor.Neutral1,
          display: 'flex',
          gap: '16px',
        }}
      >
        {activeStep === 1 && (
          <CustomButton
            disabled={disabled}
            onClick={handleBack}
            variant='outlined'
            style={{
              borderRadius: '14px',
              height: 56,
              flex: 1,
              border: `1px solid ${zeplinColor.BasicBlack}`,
              backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
            }}
            textStyle={{ color: disabled ? zeplinColor.Neutral8 : zeplinColor.BasicBlack }}
            text={'Back'}
          />
        )}
        <CustomButton
          disabled={disabled}
          onClick={handleCreateLead}
          variant={activeStep === 1 ? 'outlined' : 'contained'}
          color={'primary'}
          style={{
            borderRadius: '14px',
            height: 56,
            ...(activeStep === 1 ? { flex: 2 } : {}),
            ...(activeStep === 0 ? { width: '100%' } : {}),
            border: activeStep === 1 ? `1px solid ${zeplinColor.BasicBlack}` : 'none',
            backgroundColor: disabled
              ? zeplinColor.Neutral3
              : activeStep === 1
                ? zeplinColor.Neutral1
                : zeplinColor.Brand1,
          }}
          textStyle={{
            color: disabled
              ? zeplinColor.Neutral8
              : activeStep === 1
                ? zeplinColor.BasicBlack
                : zeplinColor.Neutral1,
          }}
          text={
            isEditMode
              ? createLoading
                ? 'Updating...'
                : 'Save'
              : createLoading
                ? 'Creating...'
                : 'Create Lead'
          }
        />
        {activeStep === 1 ? (
          <CustomButton
            disabled={disabled}
            onClick={handleGenerateAnalysis}
            variant='outlined'
            style={{
              borderRadius: '14px',
              height: 56,
              flex: 3,
              border: `1px solid ${zeplinColor.BasicBlack}`,
              backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
            }}
            textStyle={{
              color: disabled ? zeplinColor.Neutral8 : zeplinColor.BasicBlack,
            }}
            text={disabled ? 'Generating...' : 'Generate Analysis'}
          />
        ) : (
          <CustomButton
            disabled={disabled}
            onClick={handleNext}
            variant='outlined'
            color={'customSecondary'}
            style={{
              borderRadius: '14px',
              height: 56,
              width: '100%',
              border: `1px solid ${zeplinColor.BasicBlack}`,
              backgroundColor: disabled ? zeplinColor.Neutral3 : zeplinColor.Neutral1,
            }}
            textStyle={{
              color: disabled ? zeplinColor.Neutral8 : zeplinColor.BasicBlack,
            }}
            text={'Analyze Social Insights'}
          />
        )}
      </Box>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default AddLeadForm;
